import { Box, Stack, Typography, useTheme, alpha } from "@mui/material";
import { colors, shadows } from "./source.styles";
import CircleIcon from "@mui/icons-material/Circle";

const SourceDistribution = ({ data = [] }) => {
  const theme = useTheme();
  const total = data.reduce((sum, item) => sum + item.value, 0);

  // Capitalize first letter of each word
  const capitalizeWords = (str) => {
    if (!str) return str;
    return str
      .split(/[_\s-]+/)
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  // Calculate percentages and create arc paths
  const chartData = data.map((item, index) => {
    const percentage = (item.value / total) * 100;
    return {
      ...item,
      name: capitalizeWords(item.name),
      percentage,
    };
  });

  const chartColors = [
    colors.chartBlue,
    colors.chartGreen,
    colors.chartPurple,
    colors.chartOrange,
    colors.chartPink,
  ];

  return (
    <Box
      sx={{
        bgcolor: "#FFFFFF",
        borderRadius: 3,
        p: 3,
        border: `1px solid ${colors.borderLight}`,
        boxShadow: shadows.card,
        flex: 1,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Typography
        sx={{
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: 0.5,
          color: colors.textMuted,
          textTransform: "uppercase",
          mb: 3,
        }}
      >
        Source Distribution
      </Typography>

      {/* Donut Chart */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          mb: 4,
          position: "relative",
        }}
      >
        <svg width="200" height="200" viewBox="0 0 200 200">
          <defs>
            <linearGradient id="gradient-blue" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor={colors.chartBlue} />
              <stop offset="100%" stopColor="#818CF8" />
            </linearGradient>
            <linearGradient id="gradient-green" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor={colors.chartGreen} />
              <stop offset="100%" stopColor="#34D399" />
            </linearGradient>
            <linearGradient id="gradient-purple" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor={colors.chartPurple} />
              <stop offset="100%" stopColor={alpha(colors.chartPurple, 0.6)} />
            </linearGradient>
            <linearGradient id="gradient-orange" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor={colors.chartOrange} />
              <stop offset="100%" stopColor="#FB923C" />
            </linearGradient>
            <linearGradient id="gradient-pink" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor={colors.chartPink} />
              <stop offset="100%" stopColor="#F472B6" />
            </linearGradient>
          </defs>

          {/* Background circle */}
          <circle
            cx="100"
            cy="100"
            r="80"
            fill="none"
            stroke={colors.borderLight}
            strokeWidth="24"
          />

          {/* Segments */}
          {chartData.map((item, index) => {
            const startAngle = chartData
              .slice(0, index)
              .reduce((sum, d) => sum + (d.percentage * 360) / 100, 0);
            const angle = (item.percentage * 360) / 100;
            const endAngle = startAngle + angle;

            const startRad = ((startAngle - 90) * Math.PI) / 180;
            const endRad = ((endAngle - 90) * Math.PI) / 180;

            const x1 = 100 + 80 * Math.cos(startRad);
            const y1 = 100 + 80 * Math.sin(startRad);
            const x2 = 100 + 80 * Math.cos(endRad);
            const y2 = 100 + 80 * Math.sin(endRad);

            const largeArc = angle > 180 ? 1 : 0;

            return (
              <path
                key={index}
                d={`M 100 100 L ${x1} ${y1} A 80 80 0 ${largeArc} 1 ${x2} ${y2} Z`}
                fill={`url(#gradient-${index === 0 ? 'blue' : index === 1 ? 'green' : index === 2 ? 'purple' : index === 3 ? 'orange' : 'pink'})`}
                opacity="0.9"
              />
            );
          })}

          {/* Inner white circle */}
          <circle cx="100" cy="100" r="56" fill="#FFFFFF" />

          {/* Center text */}
          <text
            x="100"
            y="95"
            textAnchor="middle"
            fill={colors.textPrimary}
            fontSize="32"
            fontWeight="700"
          >
            {total.toLocaleString()}
          </text>
          <text
            x="100"
            y="112"
            textAnchor="middle"
            fill={colors.textMuted}
            fontSize="11"
            fontWeight="600"
          >
            TOTAL LEADS
          </text>
        </svg>
      </Box>

      {/* Legend */}
      <Stack spacing={2}>
        {chartData.map((item, index) => (
          <Stack
            key={index}
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <CircleIcon sx={{ fontSize: 12, color: chartColors[index] || colors.textMuted }} />
              <Typography
                sx={{
                  fontSize: 14,
                  fontWeight: 500,
                  color: colors.textPrimary,
                }}
              >
                {item.name}
              </Typography>
            </Stack>
            <Typography
              sx={{
                fontSize: 14,
                fontWeight: 700,
                color: colors.textPrimary,
              }}
            >
              {item.value} Leads
            </Typography>
          </Stack>
        ))}
      </Stack>
    </Box>
  );
};

export default SourceDistribution;
